import { FARO_CARD_ACTIONS } from "@components/common/faro-card/faro-card";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { SdbProject } from "@custom-types/project-types";
import { Box } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { shouldHideOpenProject } from "@utils/project-utils";
import { ProjectOptionsMenu } from "@components/project/project-options-menu";
import { ProjectOpenButton } from "@components/project/project-open-button";

/** Determines the context or places where the the ProjectActions component can be used */
export type ProjectActionContext =
  | "project-list-view"
  | "project-tiles-view"
  | "project-map-view"
  | "projects-menu-option-mobile"
  | "group-project-list-view"
  | "member-group-project-list-view";

interface Props {
  /** Project entity */
  project: SdbProject | FaroCoreApiTypes.ProjectSummary;

  /** Determines on which context the ProjectActions component is being used */
  contextType: ProjectActionContext;
}

/** Project actions buttons */
export function ProjectActions({ project, contextType }: Props): JSX.Element {
  const sdbProject = useAppSelector(getProjectByIdSelector(project.id));
  const shouldShowOpenProjectButton = !shouldHideOpenProject(sdbProject);
  const shouldShowDivider = shouldShowOpenProjectButton && sdbProject !== null;

  return (
    <Box
      component="div"
      className={FARO_CARD_ACTIONS}
      sx={{
        display: "flex",
        alignItems: "center",
        // When the context is "project-tiles-view" the visibility is overwritten in the hover of the parent component
        // Otherwise visibility is performed in general faro table component
        visibility: contextType === "project-tiles-view" ? "hidden" : undefined,
      }}
    >
      {sdbProject !== null && shouldShowOpenProjectButton && (
        <ProjectOpenButton project={sdbProject} contextType={contextType} />
      )}

      {shouldShowDivider && <SphereActionDivider />}
      {sdbProject !== null && (
        <ProjectOptionsMenu project={sdbProject} contextType={contextType} />
      )}
    </Box>
  );
}
